import { IconButton } from "@material-ui/core";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { PAGINATE_BY } from "@saleor/config";
import { configurationMenuUrl } from "@saleor/configuration";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { maybe } from "@saleor/misc";
import SoldoutCustomPageListPage from "@saleor/soldoutcustompage/components/SoldoutCustomPageListPage";
import { useSoldoutproductvariantsListQuery } from "@saleor/soldoutcustompage/queries";
import {
  soldoutCustomPageListUrl,
  SoldoutCustomPageListUrlDialog,
  SoldoutCustomPageListUrlQueryParams
} from "@saleor/soldoutcustompage/urls";
import {
  OrderDirection,
  SoldoutInCustompageSortField
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import React from "react";

import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";

interface SoldoutCustomPageListProps {
  params: SoldoutCustomPageListUrlQueryParams;
}

const SoldoutCustomPageList: React.FC<SoldoutCustomPageListProps> = ({
  params
}) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const shop = useShop();

  const paginationState = createPaginationState(PAGINATE_BY, params);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params]
  );
  const { data, loading } = useSoldoutproductvariantsListQuery({
    variables: {
      sortBy: {
        direction: OrderDirection.ASC,
        field: SoldoutInCustompageSortField.CUSTOMPAGE_URL
      },
      ...queryVariables
    }
  });
  const currencySymbol = maybe(() => shop.defaultCurrency, "USD");

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.soldoutInCustompage.pageInfo),
    paginationState,
    params
  );
  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);
  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    cleanupFn: reset,
    createUrl: soldoutCustomPageListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const handleTabChange = (tab: number) => {
    reset();
    navigate(
      soldoutCustomPageListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };
  const [openModal, closeModal] = createDialogActionHandlers<
    SoldoutCustomPageListUrlDialog,
    SoldoutCustomPageListUrlQueryParams
  >(navigate, soldoutCustomPageListUrl, params);
  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };
  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    reset();
    navigate(soldoutCustomPageListUrl());
  };
  return (
    <>
      <SoldoutCustomPageListPage
        toatlCount={maybe(() => data.soldoutInCustompage.totalCount, 0)}
        soldoutCustomPages={maybe(() =>
          data?.soldoutInCustompage?.edges?.map(edge => edge.node)
        )}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        disabled={loading}
        filterOpts={getFilterOpts(params)}
        initialSearch={params.query || ""}
        isChecked={isSelected}
        onAll={resetFilters}
        onBack={() => navigate(configurationMenuUrl)}
        onFilterChange={changeFilters}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onSearchChange={handleSearchChange}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        pageInfo={pageInfo}
        selected={listElements.length}
        tabs={tabs.map(tab => tab.name)}
        toggle={toggle}
        toggleAll={toggleAll}
        onAdd={null}
        onRowClick={null}
        toolbar={<IconButton color="primary"></IconButton>}
      />
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={maybe(() => tabs[currentTab - 1].name, "...")}
      />
    </>
  );
};
export default SoldoutCustomPageList;
